import { gql } from "@apollo/client";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSearchSelect } from "src/components/Inputs/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { UseOrganizationType } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { formatIsoDateToOrgDate } from "src/services/format";
import { validateWithZod } from "src/services/formValidations";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import {
  buildRuleImportErrors,
  deactivateRules,
  partitionRulesForImport,
  RuleImportError,
} from "./helpers";

const FormSchema = z.object({
  formTemplateId: z
    .string({ message: "This field is required" })
    .min(1, { message: "This field is required" }),
});
type FormType = z.infer<typeof FormSchema>;

const GET_SOURCE_FORM_TEMPLATES = gql`
  query GetFormTemplatesForRuleImport(
    $organizationId: uuid!
    $targetFormTemplateId: uuid!
  ) {
    form_template(
      where: {
        id: { _neq: $targetFormTemplateId }
        enrollment_period: { organization_id: { _eq: $organizationId } }
        deleted_at: { _is_null: true }
      }
    ) {
      id
      name
      updated_at
      form_template_rule_id
      form_engine_rules_aggregate {
        aggregate {
          count
        }
      }
      offer_configurations_aggregate {
        aggregate {
          count
        }
      }
      enrollment_period {
        id
        name
      }
    }
  }
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  targetFormTemplateId: string;
  organization: UseOrganizationType;
  onComplete: (errors: RuleImportError[]) => void;
};

export const ImportRulesFromOtherDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  targetFormTemplateId,
  organization,
  onComplete,
}) => {
  const toast = useAvelaToast();

  const { remoteData } = useRemoteDataQuery<
    GQL.GetFormTemplatesForRuleImport,
    GQL.GetFormTemplatesForRuleImportVariables
  >(GET_SOURCE_FORM_TEMPLATES, {
    variables: {
      organizationId: organization.toNullable()?.id ?? "",
      targetFormTemplateId,
    },
    skip: !organization.hasData(),
    fetchPolicy: "cache-and-network",
  });

  const handleImportFromOther = React.useCallback(
    async (values: FormType) => {
      toast.closeAll();

      try {
        const exportResult = (
          await FSR.exportRuleConfiguration({
            path: { formTemplateId: values.formTemplateId },
          })
        ).data?.data;

        if (!exportResult) {
          toast.error({
            title: "Something went wrong",
            description: "Please try again",
          });
          return;
        }

        if (exportResult.result === "invalid") {
          toast.error({
            title: "The source form's rules could not be read",
            description: "Review the specific errors and try again",
          });
          return onComplete(buildRuleImportErrors(exportResult.errorGroups));
        }

        const warn = () =>
          toast({
            title: "Some rules were not imported",
            description: "Review the specific errors and try again",
            status: "warning",
          });

        const { validRules, parseErrors, crossRuleErrors } =
          partitionRulesForImport(exportResult.rules);

        if (crossRuleErrors.length > 0) {
          warn();
          return onComplete([...parseErrors, ...crossRuleErrors]);
        }

        if (validRules.length === 0) {
          warn();
          return onComplete(parseErrors);
        }

        const rulesToImport = deactivateRules(validRules);

        const importResult = (
          await FSR.importRuleConfiguration({
            path: { formTemplateId: targetFormTemplateId },
            body: rulesToImport as FSR.ImportRuleConfigurationData["body"],
          })
        ).data?.data;

        if (!importResult) {
          toast.error({
            title: "Something went wrong",
            description: "Please try again",
          });
          return;
        }

        if (importResult.result === "invalid") {
          warn();
          return onComplete([
            ...parseErrors,
            ...buildRuleImportErrors(importResult.errorGroups, rulesToImport),
          ]);
        }

        if (parseErrors.length > 0) {
          warn();
          return onComplete(parseErrors);
        }

        const sourceFormTemplate = remoteData
          .toNullable()
          ?.form_template.find((ft) => ft.id === values.formTemplateId);

        toast({
          title: "Imported from existing",
          description: sourceFormTemplate
            ? `Rules imported from ${sourceFormTemplate.name}`
            : "Rules imported",
          status: "info",
          isClosable: true,
        });
        return onComplete([]);
      } catch (error: any) {
        if (error?.error?.message) {
          toast.error({
            title: "Something went wrong",
            description: error.error.message,
          });
        } else {
          toast.genericError();
        }
      } finally {
        onClose();
      }
    },
    [onClose, onComplete, remoteData, targetFormTemplateId, toast]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <Formik<FormType>
        initialValues={{ formTemplateId: "" }}
        onSubmit={handleImportFromOther}
        validate={validateWithZod(FormSchema)}
      >
        {(formik) => (
          <ModalContent as={Form} noValidate>
            <ModalHeader>Import rules</ModalHeader>
            <ModalBody>
              <Flex direction="column" gap="4">
                <Text>
                  Select a form to import rules from it.{" "}
                  <Box fontWeight="bold" as="span">
                    The import will update rules that share a key.
                  </Box>
                </Text>
                <RemoteDataView
                  remoteData={remoteData}
                  error={(error) => <GenericError error={error} />}
                  loading={<Skeleton width="100%" height="2rem" />}
                >
                  {(data) => (
                    <FormikSearchSelect
                      name="formTemplateId"
                      label="Form to import rules from"
                      items={data.form_template
                        .filter(
                          (ft) =>
                            (ft.form_engine_rules_aggregate.aggregate?.count ??
                              0) > 0 ||
                            (ft.offer_configurations_aggregate.aggregate
                              ?.count ?? 0) > 0 ||
                            ft.form_template_rule_id !== null
                        )
                        .map((ft) => ({
                          key: ft.id,
                          label: ft.name,
                          description: (
                            <>
                              <Text>{ft.enrollment_period.name}</Text>
                              <Text>
                                Last modified on{" "}
                                {formatIsoDateToOrgDate(
                                  ft.updated_at,
                                  organization
                                )}
                              </Text>
                            </>
                          ),
                        }))}
                    />
                  )}
                </RemoteDataView>
              </Flex>
            </ModalBody>
            <ModalFooter gap="3">
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={formik.isSubmitting}>
                Import
              </Button>
            </ModalFooter>
          </ModalContent>
        )}
      </Formik>
    </Modal>
  );
};
